/**
 * AnnuaireCollisionCanvas — Vue "Mode Collision" pour l'Annuaire.
 *
 * V23.15.60 — Chaque entité de l'annuaire devient un nœud vivant qui dérive
 * organiquement dans le canvas. Quand deux nœuds passent suffisamment près
 * l'un de l'autre (>1.2 s de proximité), Noam interroge son IA doctrinale
 * pour détecter une opportunité ou un risque systémique CACHÉ entre elles.
 *
 * Design :
 *   - Canvas SVG natif (léger, pas de React Flow)
 *   - Drift multi-fréquence (chaque nœud a sa propre orbite lente)
 *   - Débounce strict + quota 5 analyses par session pour préserver la clé LLM
 *   - Cache 24h côté backend par paire (workspace_collisions)
 *   - Rendu factuel : chaque collision doit citer un fait vérifiable
 *
 * Props :
 *   - entities : [{id, name, type, sector?, status?}]
 *   - onSelectEntity? : (id) => void
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useCollisions } from "@/hooks/useCollisions";
import { CollisionBadge, CollisionPanel } from "@/components/CollisionBadge";
import { Building2, User, Landmark, Coins, Users2, Heart, ShieldCheck, Globe2, Sparkles, RotateCcw, Loader2, Info } from "lucide-react";

const TYPE_ICONS = {
  holding: Landmark,
  societe: Building2,
  personne: User,
  institution: ShieldCheck,
  fonds: Coins,
  famille: Heart,
  ong: Users2,
  etat: Globe2,
};

const CANVAS_W = 1000;
const CANVAS_H = 600;
const NODE_RADIUS = 22;
const DRIFT_SPEED_MIN = 0.05;
const DRIFT_SPEED_MAX = 0.12;

function seededRandom(seed) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

function initEntitiesLayout(entities) {
  const rand = seededRandom(entities.length * 137);
  const cx = CANVAS_W / 2;
  const cy = CANVAS_H / 2;
  const R = Math.min(cx, cy) - NODE_RADIUS - 30;
  return entities.map((e, i) => {
    const angle = (i / Math.max(1, entities.length)) * Math.PI * 2 + rand() * 0.5;
    const radius = R * (0.55 + rand() * 0.4);
    return {
      ...e,
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
      // Chaque nœud a 2 harmoniques d'oscillation (fréquence + amplitude)
      phaseA: rand() * Math.PI * 2,
      phaseB: rand() * Math.PI * 2,
      freqA: DRIFT_SPEED_MIN + rand() * (DRIFT_SPEED_MAX - DRIFT_SPEED_MIN),
      freqB: DRIFT_SPEED_MIN + rand() * (DRIFT_SPEED_MAX - DRIFT_SPEED_MIN),
      ampA: 40 + rand() * 30,
      ampB: 25 + rand() * 20,
    };
  });
}

export default function AnnuaireCollisionCanvas({ entities = [], onSelectEntity }) {
  const [layout, setLayout] = useState(() => initEntitiesLayout(entities));
  const [tick, setTick] = useState(0);
  const [openCollision, setOpenCollision] = useState(null);
  const rafRef = useRef(null);
  const startTimeRef = useRef(performance.now());
  const posRef = useRef({}); // id -> { x, y } (positions "vives")

  // Reset positions on entities list change
  useEffect(() => {
    setLayout(initEntitiesLayout(entities));
    startTimeRef.current = performance.now();
  }, [entities]);

  // Animation loop — drift organique via oscillations sinusoïdales
  useEffect(() => {
    let running = true;
    const step = () => {
      if (!running) return;
      setTick((t) => (t + 1) % 100000);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      running = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const t = (performance.now() - startTimeRef.current) / 1000; // seconds

  // Positions "vives" recalculées à chaque frame (mémoïsé par tick)
  const livePositions = useMemo(() => {
    const map = {};
    layout.forEach((n) => {
      const dx = Math.cos(t * n.freqA + n.phaseA) * n.ampA + Math.cos(t * n.freqB * 0.7 + n.phaseB) * n.ampB * 0.4;
      const dy = Math.sin(t * n.freqB + n.phaseB) * n.ampB + Math.sin(t * n.freqA * 0.9 + n.phaseA) * n.ampA * 0.3;
      map[n.id] = { x: n.x + dx, y: n.y + dy };
    });
    posRef.current = map;
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, layout]);

  // useCollisions callbacks stables
  const getEntities = useCallback(() => layout.map((n) => ({
    id: n.id,
    name: n.name,
    kind: n.type,
    role: n.sector || n.status,
  })), [layout]);
  const getPosition = useCallback((e) => posRef.current[e.id] || null, []);

  const {
    collisionsByEntity,
    analysisCount,
    busyPair,
    quotaExceeded,
    maxAnalyses,
    resetSession,
  } = useCollisions({
    active: layout.length >= 2,
    surface: "annuaire",
    getEntities,
    getPosition,
  });

  if (!entities || entities.length < 2) {
    return (
      <div className="text-center py-16 text-zinc-500 text-sm" data-testid="annuaire-collision-empty">
        Au moins 2 entités sont nécessaires pour activer le mode Collision.
      </div>
    );
  }

  // Lignes de connexion entre paires en collision (pour rendu visuel élégant)
  const collisionLines = [];
  const seen = new Set();
  Object.entries(collisionsByEntity).forEach(([entityId, list]) => {
    (list || []).forEach((c) => {
      const key = c.pair_key || Object.values(c.entity_a && c.entity_b ? [c.entity_a.id, c.entity_b.id].sort() : []).join("|");
      if (seen.has(key)) return;
      seen.add(key);
      const aid = c.entity_a?.id;
      const bid = c.entity_b?.id;
      const pa = livePositions[aid];
      const pb = livePositions[bid];
      if (!pa || !pb) return;
      collisionLines.push({
        key,
        x1: pa.x, y1: pa.y,
        x2: pb.x, y2: pb.y,
        kind: c.kind,
      });
    });
  });

  return (
    <div className="relative" data-testid="annuaire-collision-canvas">
      {/* Bandeau info + quota */}
      <div className="absolute top-3 left-3 right-3 z-10 flex items-center justify-between gap-2 pointer-events-none">
        <div className="inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded bg-black/60 backdrop-blur-sm border border-white/10 text-zinc-300 pointer-events-auto">
          <Sparkles className="w-3 h-3 text-[#EF9F27]" />
          Mode Collision · les entités dérivent, l&apos;IA détecte les liens factuels quand elles se croisent
        </div>
        <div className="inline-flex items-center gap-2 pointer-events-auto">
          {busyPair && (
            <span className="text-[10px] px-2 py-1 rounded bg-[#EF9F27]/15 border border-[#EF9F27]/40 text-[#EF9F27] inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Analyse…
            </span>
          )}
          <span
            className={`text-[10px] px-2 py-1 rounded border tabular-nums ${
              quotaExceeded
                ? "bg-[#E24B4A]/15 border-[#E24B4A]/40 text-[#E24B4A]"
                : "bg-black/60 border-white/10 text-zinc-400"
            }`}
            data-testid="annuaire-collision-quota"
          >
            {analysisCount}/{maxAnalyses}
          </span>
          <button
            type="button"
            onClick={resetSession}
            className="text-[10px] px-2 py-1 rounded bg-black/60 border border-white/10 text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1"
            title="Réinitialiser la session (nouvelles analyses possibles)"
            data-testid="annuaire-collision-reset"
          >
            <RotateCcw className="w-3 h-3" /> Réinitialiser
          </button>
        </div>
      </div>

      <svg
        viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full"
        style={{
          height: CANVAS_H,
          background: "radial-gradient(ellipse at center, rgba(127,119,221,0.05) 0%, rgba(10,10,10,0) 70%)",
        }}
        data-testid="annuaire-collision-svg"
      >
        <defs>
          <filter id="ac-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id="ac-node-bg" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#378ADD" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#378ADD" stopOpacity="0.05" />
          </radialGradient>
        </defs>

        {/* Lignes de collision */}
        {collisionLines.map((l) => {
          const stroke = l.kind === "opportunity" ? "#1D9E75" : l.kind === "risk" ? "#E24B4A" : "#7F77DD";
          return (
            <line
              key={l.key}
              x1={l.x1}
              y1={l.y1}
              x2={l.x2}
              y2={l.y2}
              stroke={stroke}
              strokeWidth="1.5"
              strokeDasharray="4 4"
              opacity="0.55"
              filter="url(#ac-glow)"
            />
          );
        })}

        {/* Entités */}
        {layout.map((n) => {
          const pos = livePositions[n.id] || { x: n.x, y: n.y };
          const cols = collisionsByEntity[n.id] || [];
          const Icon = TYPE_ICONS[n.type] || Building2;
          const hasCollision = cols.length > 0;
          const bestKind = hasCollision
            ? cols.reduce((a, b) => ((a.score || 0) > (b.score || 0) ? a : b)).kind
            : null;
          const ringColor = bestKind === "opportunity" ? "#1D9E75" : bestKind === "risk" ? "#E24B4A" : "#378ADD";

          return (
            <g
              key={n.id}
              transform={`translate(${pos.x}, ${pos.y})`}
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                if (hasCollision) {
                  const best = [...cols].sort((a, b) => (b.score || 0) - (a.score || 0))[0];
                  setOpenCollision(best);
                } else if (onSelectEntity) {
                  onSelectEntity(n.id);
                }
              }}
              data-testid={`annuaire-collision-node-${n.id}`}
            >
              {hasCollision && (
                <circle
                  r={NODE_RADIUS + 8}
                  fill="none"
                  stroke={ringColor}
                  strokeWidth="1.5"
                  strokeDasharray="3 3"
                  opacity="0.7"
                  filter="url(#ac-glow)"
                >
                  <animate attributeName="r" values={`${NODE_RADIUS + 6};${NODE_RADIUS + 12};${NODE_RADIUS + 6}`} dur="2.5s" repeatCount="indefinite" />
                </circle>
              )}
              <circle
                r={NODE_RADIUS}
                fill="url(#ac-node-bg)"
                stroke={hasCollision ? ringColor : "#378ADD66"}
                strokeWidth={hasCollision ? "1.8" : "1"}
              />
              <foreignObject x={-14} y={-14} width={28} height={28} pointerEvents="none">
                <div className="w-full h-full flex items-center justify-center">
                  <Icon className="w-3.5 h-3.5" style={{ color: hasCollision ? ringColor : "#378ADD" }} />
                </div>
              </foreignObject>
              <text
                y={NODE_RADIUS + 14}
                fontSize="10"
                fill="#D4D4D8"
                textAnchor="middle"
                fontWeight="500"
                style={{ pointerEvents: "none" }}
              >
                {(n.name || "").slice(0, 20)}
              </text>
              {hasCollision && (
                <foreignObject x={NODE_RADIUS - 6} y={-NODE_RADIUS - 10} width={40} height={20} pointerEvents="none">
                  <CollisionBadge collisions={cols} />
                </foreignObject>
              )}
            </g>
          );
        })}
      </svg>

      {/* Panneau détail collision */}
      {openCollision && (
        <CollisionPanel
          collision={openCollision}
          onClose={() => setOpenCollision(null)}
        />
      )}

      {/* Note pédagogique bas-canvas */}
      <div className="absolute bottom-2 left-3 right-3 pointer-events-none">
        <div className="inline-flex items-start gap-1.5 text-[9px] text-zinc-500 max-w-2xl">
          <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span>
            Chaque analyse s&apos;appuie sur un fait vérifiable (partenariat, régulation, dépendance publique).
            Les requêtes de vérification apparaissent dans le panneau détail.
          </span>
        </div>
      </div>
    </div>
  );
}
